import type { ShiftProductionFormPayload } from "../../../shared/shift-production-form/contracts";
import { type ShiftFormValidationIssue, validationMessage } from "./form-state";

const checklistLabels = {
  TANK_AND_PIPING: "Tank ve Boru Tesisat",
  MOVING_PARTS: "Hareketli Aksam",
  GUARDS_SENSORS_SWITCHES: "Muhafaza / Sensör / Switch",
  MOLD: "Kalıp Adeti",
  REEL_PACKAGING_LABEL_BOX: "Bobin / Ambalaj / Etiket / Koli",
} as const;

export function ShiftFormChecklist({
  items,
  explanation,
  validationIssues,
  onChange,
}: {
  items: ShiftProductionFormPayload["checklist"];
  explanation: string;
  validationIssues: readonly ShiftFormValidationIssue[];
  onChange: (
    items: ShiftProductionFormPayload["checklist"],
    legacyExplanation?: string,
  ) => void;
}) {
  const firstUnsuitable = items.findIndex(
    (item) => item.state === "UNSUITABLE",
  );
  return (
    <div className="shift-paper-scroll" data-shift-diff-path="checklist">
      <table className="shift-paper-table shift-check-table">
        <thead>
          <tr>
            {items.map((item) => (
              <th key={item.key} scope="col">
                {checklistLabels[item.key]}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {items.map((item, index) => (
              <td key={item.key}>
                <div
                  className="shift-check-options"
                  role="group"
                  aria-label={checklistLabels[item.key]}
                  data-shift-validation={`checklist.${item.key}.state`}
                  data-shift-diff-path={`checklist.${item.key}.state`}
                  aria-invalid={Boolean(validationMessage(validationIssues, `checklist.${item.key}.state`))}
                >
                  {(
                    [
                      ["SUITABLE", "Uygun"],
                      ["UNSUITABLE", "Değil"],
                    ] as const
                  ).map(([state, label]) => (
                    <label key={state}>
                      <input
                        type="checkbox"
                        checked={item.state === state}
                        onChange={() =>
                          onChange(
                            items.map((entry, entryIndex) =>
                              entryIndex === index
                                ? {
                                    ...entry,
                                    state: item.state === state ? null : state,
                                  }
                                : entry,
                            ),
                          )
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <InlineError message={validationMessage(validationIssues, `checklist.${item.key}.state`)} />
              </td>
            ))}
          </tr>
          {items.map((item, index) =>
            item.state === "UNSUITABLE" ? (
              <tr key={item.key} className="shift-check-explanation-row">
                <td colSpan={items.length}>
                  <label className="shift-check-item-explanation">
                    <span>
                      {checklistLabels[item.key]} — Uygunsuzluk Açıklaması:
                    </span>
                    <textarea
                      data-shift-validation={`checklist.${item.key}.explanation`}
                      data-shift-diff-path={`checklist.${item.key}.explanation`}
                      aria-invalid={Boolean(validationMessage(validationIssues, `checklist.${item.key}.explanation`))}
                      aria-describedby={validationMessage(validationIssues, `checklist.${item.key}.explanation`) ? `shift-error-checklist-${item.key}` : undefined}
                      aria-label={`${checklistLabels[item.key]} / Uygunsuzluk Açıklaması`}
                      placeholder="Uygunsuzluğu açıklayın"
                      rows={4}
                      value={
                        item.explanation ||
                        (index === firstUnsuitable ? explanation : "")
                      }
                      onChange={(event) =>
                        onChange(
                          items.map((entry, entryIndex) =>
                            entryIndex === index
                              ? { ...entry, explanation: event.target.value }
                              : entry,
                          ),
                          index === firstUnsuitable && !item.explanation
                            ? ""
                            : undefined,
                        )
                      }
                    />
                    <InlineError id={`shift-error-checklist-${item.key}`} message={validationMessage(validationIssues, `checklist.${item.key}.explanation`)} />
                  </label>
                </td>
              </tr>
            ) : null,
          )}
        </tbody>
      </table>
    </div>
  );
}

function InlineError({ id, message }: { id?: string; message: string | null }) {
  return message ? <small id={id} className="shift-field-error" role="alert">{message}</small> : null;
}
